import { registerBlock } from "../registry";
import { makeId } from "../helpers";
import type { ContentBlock, ContentTag } from "../types";

// ─── Article 1: Male Factor Infertility ─────────────────────────────

registerBlock({
  id: makeId("new-article-male-factor"),
  kind: "article",
  tags: ["sperm", "semen-analysis", "his", "testing"] as ContentTag[],
  title: "Male factor infertility: what the numbers mean",
  subtitle: "Understanding his side of the equation - and what to do next.",
  readTimeMinutes: 7,
  body: {
    quickSummary: [
      {
        title: "It's half the picture.",
        description:
          "Male factor contributes to about 40-50% of all infertility cases, yet it's often the last thing tested.",
      },
      {
        title: "One test tells a lot.",
        description:
          "A semen analysis measures count, motility, and morphology - the three pillars of sperm health.",
      },
      {
        title: "Most cases are treatable.",
        description:
          "From lifestyle changes to surgical repair, the majority of male factor issues have effective solutions.",
      },
    ],
    sections: [
      {
        title: "What is male factor infertility?",
        content:
          "Male factor infertility means something about the sperm is making it harder to conceive. It could be that there aren't enough sperm, they aren't swimming well, or they're shaped in a way that makes fertilization difficult. About 40-50% of infertility cases involve a male factor, either on its own or alongside a female factor. Despite how common it is, many couples spend months or years investigating only the female partner before a semen analysis is ever ordered. Getting tested early saves time, money, and heartache.",
        bulletPoints: [
          "Affects roughly 1 in 8 couples trying to conceive",
          "Can be the sole cause or a contributing factor alongside female issues",
          "Often has no obvious symptoms - most men feel perfectly healthy",
          "A simple semen analysis is the first and most important step",
        ],
      },
      {
        title: "The semen analysis explained",
        content:
          "A semen analysis is the cornerstone test for evaluating male fertility. It's non-invasive and typically costs between $100 and $300. The sample is collected via ejaculation (usually at the clinic or at home with a special collection kit) and examined in the lab within an hour. The lab evaluates several parameters, but three matter most: sperm count (concentration), motility (movement), and morphology (shape). The World Health Organization sets reference values, but your reproductive endocrinologist will interpret your results in context.",
        bulletPoints: [
          "Concentration: at least 15 million sperm per milliliter is considered normal",
          "Total motility: 40% or more of sperm should be moving",
          "Progressive motility: 32% or more should be swimming forward effectively",
          "Morphology (strict criteria): 4% or more normal forms is within range",
          "Volume: 1.5 mL or more per ejaculate",
        ],
      },
      {
        title: "Common causes of male factor",
        content:
          "There are many reasons sperm parameters can be off. Some are structural, some are hormonal, and some are related to lifestyle. A varicocele - an enlarged vein in the scrotum - is the most common correctable cause and is found in about 35-40% of men with primary infertility. Hormonal imbalances, prior infections, genetic conditions, and certain medications can also play a role. Heat exposure (hot tubs, laptops on laps, tight clothing) and lifestyle factors like smoking, heavy alcohol use, and obesity are well-documented contributors.",
        bulletPoints: [
          "Varicocele: the most common treatable cause, found in 35-40% of cases",
          "Hormonal issues: low testosterone, elevated prolactin, or thyroid problems",
          "Lifestyle factors: smoking, excessive alcohol, obesity, and heat exposure",
          "Medications: testosterone supplementation, certain antidepressants, and anabolic steroids",
          "Genetic factors: Y-chromosome microdeletions, Klinefelter syndrome",
        ],
      },
      {
        title: "When to see a reproductive urologist",
        content:
          "If a semen analysis comes back abnormal, the next step is usually a visit to a reproductive urologist - a specialist who focuses on male fertility. This is different from a general urologist. A reproductive urologist will do a physical exam, review your medical and medication history, and may order additional bloodwork (hormone levels including FSH, LH, testosterone, and prolactin). They might also recommend a scrotal ultrasound to check for varicoceles or other structural issues. If the first semen analysis is abnormal, it's standard practice to repeat it 2-3 months later, since sperm production takes about 72 days and results can fluctuate.",
        bulletPoints: [
          "Seek a reproductive urologist specifically, not just a general urologist",
          "Expect a physical exam, hormone panel, and possibly scrotal ultrasound",
          "A single abnormal result should always be confirmed with a repeat test",
          "Bring a complete medication list - some common drugs impair sperm production",
        ],
      },
      {
        title: "Treatment options",
        content:
          "Treatment depends entirely on the cause. Varicocele repair (varicocelectomy) can improve sperm parameters in 60-70% of cases. Hormonal imbalances can often be corrected with medications like clomiphene citrate or anastrozole. Lifestyle modifications - losing weight, quitting smoking, reducing alcohol, and avoiding heat exposure - can meaningfully improve numbers over 3-6 months. When natural conception remains difficult, assisted reproduction offers powerful options. Intrauterine insemination (IUI) can help with mild male factor, while IVF with intracytoplasmic sperm injection (ICSI) can achieve fertilization even with very low counts or poor motility. In cases of obstructive azoospermia (no sperm in the ejaculate due to a blockage), surgical sperm retrieval procedures like TESE can find sperm directly from the testicle.",
        bulletPoints: [
          "Varicocele repair improves parameters in 60-70% of men",
          "Clomiphene citrate can boost sperm production in men with hormonal imbalances",
          "Lifestyle changes show measurable improvements within 3-6 months",
          "ICSI allows fertilization with even a single healthy sperm",
          "Surgical sperm retrieval (TESE/micro-TESE) is an option for azoospermia",
        ],
      },
      {
        title: "The emotional side for men",
        content:
          "Male factor infertility can hit hard emotionally, and men often struggle in silence. There's a cultural expectation that fertility is somehow tied to masculinity, which can make a diagnosis feel deeply personal - even shameful. Many men report feeling guilty that they're the reason the couple can't conceive, or helpless because they can't fix the problem quickly. It's important to know that male factor infertility is a medical condition, not a reflection of who you are. Talking to your partner openly, seeking support from a therapist who specializes in fertility, or joining a men's fertility support group can make a real difference. You don't have to carry this alone.",
        bulletPoints: [
          "Feelings of guilt, shame, and inadequacy are common - and completely normal",
          "Fertility is a medical issue, not a measure of masculinity",
          "Couples therapy or individual therapy with a fertility specialist helps",
          "Online communities and support groups exist specifically for men",
          "Open communication with your partner strengthens both the relationship and the process",
        ],
      },
    ],
    references: [
      {
        authors: "World Health Organization",
        title:
          "WHO laboratory manual for the examination and processing of human semen, 6th edition (2021)",
      },
      {
        authors: "Agarwal et al.",
        title: "Male infertility. The Lancet (2021)",
      },
      {
        authors: "Practice Committee of ASRM",
        title:
          "Diagnostic evaluation of the infertile male: a committee opinion. Fertility and Sterility (2015)",
      },
    ],
  },
} satisfies ContentBlock<"article">);

// ─── Article 2: Recurrent Miscarriage ───────────────────────────────

registerBlock({
  id: makeId("new-article-recurrent-miscarriage"),
  kind: "article",
  tags: ["her", "emotional", "testing", "practical"] as ContentTag[],
  title: "Recurrent miscarriage: when loss repeats",
  subtitle: "Understanding why it happens and what can be done.",
  readTimeMinutes: 8,
  body: {
    quickSummary: [
      {
        title: "More common than you think.",
        description:
          "About 1-2% of couples experience two or more pregnancy losses, and a cause can be identified in roughly half of cases.",
      },
      {
        title: "Testing can find answers.",
        description:
          "A thorough workup including karyotyping, uterine evaluation, and blood panels can uncover treatable causes.",
      },
      {
        title: "Hope is real.",
        description:
          "Even after three losses with no identified cause, the chance of a successful next pregnancy is still 60-75%.",
      },
    ],
    sections: [
      {
        title: "What counts as recurrent miscarriage?",
        content:
          "Recurrent pregnancy loss (RPL) is traditionally defined as two or more clinical pregnancy losses before 20 weeks of gestation. The American Society for Reproductive Medicine (ASRM) updated the definition in 2020 to recommend evaluation after two losses rather than waiting for three, which was the older standard. This matters because each loss takes a physical and emotional toll, and earlier investigation means earlier answers. It's worth knowing that a single miscarriage is extremely common - occurring in 15-25% of recognized pregnancies - and usually due to a random chromosomal error in the embryo. Recurrent loss, however, suggests there may be an underlying factor worth investigating.",
        bulletPoints: [
          "Defined as two or more pregnancy losses before 20 weeks",
          "ASRM recommends evaluation after two losses, not three",
          "A single miscarriage is common (15-25% of pregnancies) and usually random",
          "Recurrent loss affects about 1-2% of couples",
        ],
      },
      {
        title: "Possible causes",
        content:
          "The causes of recurrent miscarriage fall into several categories, and sometimes more than one factor is at play. Chromosomal abnormalities in the embryo are the most common cause at any age, and become more frequent as egg quality declines with age. Uterine structural issues - such as a septum, fibroids, or polyps - can prevent proper implantation or growth. Hormonal factors like uncontrolled thyroid disease, unmanaged diabetes, or progesterone insufficiency can also contribute. Antiphospholipid syndrome (APS), an autoimmune condition that causes blood clotting, is one of the most important treatable causes and is found in about 15% of women with recurrent loss. Other immune factors, thrombophilias, and lifestyle factors (smoking, heavy alcohol use, significant obesity) round out the picture.",
        bulletPoints: [
          "Chromosomal: random embryo errors or parental balanced translocations",
          "Uterine: septum, fibroids, polyps, or Asherman syndrome (scarring)",
          "Hormonal: thyroid dysfunction, uncontrolled diabetes, progesterone deficiency",
          "Immunological: antiphospholipid syndrome (found in about 15% of RPL cases)",
          "Thrombophilias: Factor V Leiden, prothrombin gene mutation",
        ],
      },
      {
        title: "The testing workup",
        content:
          "A comprehensive RPL workup aims to identify any treatable cause. Your doctor will likely start with blood tests for both partners, including a karyotype (chromosome analysis) to look for balanced translocations - structural chromosome rearrangements that don't affect the carrier's health but can cause embryos to have abnormal chromosomes. You'll also be tested for antiphospholipid antibodies (lupus anticoagulant, anticardiolipin, and beta-2 glycoprotein), thyroid function, and possibly a thrombophilia panel. Imaging of the uterus is important too - a saline infusion sonogram (SIS), hysteroscopy, or MRI can reveal structural abnormalities that might be correctable. If available, genetic testing of miscarriage tissue (products of conception) can determine whether the loss was due to a chromosomal issue in that specific pregnancy.",
        bulletPoints: [
          "Karyotype for both partners to check for balanced translocations",
          "Antiphospholipid antibody panel (tested twice, 12 weeks apart)",
          "Thyroid panel (TSH, free T4) and hemoglobin A1c",
          "Uterine imaging: SIS, hysteroscopy, or MRI",
          "Genetic testing of miscarriage tissue when possible",
        ],
      },
      {
        title: "Treatment approaches",
        content:
          "Treatment is tailored to whatever cause is identified. Antiphospholipid syndrome is treated with low-dose aspirin and heparin during pregnancy, which dramatically improves outcomes - live birth rates go from about 10% untreated to 70% or higher with treatment. Uterine septum can be surgically corrected via hysteroscopy. Thyroid disease and diabetes are managed with medication to optimize levels before and during pregnancy. If a balanced translocation is found in either partner, IVF with preimplantation genetic testing for structural rearrangements (PGT-SR) can select embryos with normal chromosomes. Progesterone supplementation in early pregnancy is widely used, though evidence for its benefit is strongest in women with a history of three or more losses.",
        bulletPoints: [
          "APS: low-dose aspirin plus heparin improves live birth to 70%+",
          "Uterine septum: hysteroscopic correction is often straightforward",
          "Thyroid/diabetes: optimize levels before conceiving",
          "Balanced translocation: IVF with PGT-SR to select normal embryos",
          "Progesterone: most beneficial after three or more losses",
        ],
      },
      {
        title: "The emotional toll and grief",
        content:
          "Recurrent miscarriage is one of the most emotionally devastating experiences in reproductive medicine. Each loss carries its own grief, and repeated losses can compound into a weight that feels unbearable. Many people describe feeling isolated - friends and family may not understand why you keep trying, or may say well-meaning but hurtful things like 'at least you can get pregnant.' Anxiety during subsequent pregnancies is almost universal, with every twinge, every bathroom visit, every ultrasound appointment carrying intense fear. This is a normal response to repeated trauma, not a sign of weakness. Professional support from a therapist experienced in pregnancy loss, peer support groups (like those offered by organizations such as the Miscarriage Association or RESOLVE), and open conversations with your partner are all important parts of healing.",
        bulletPoints: [
          "Grief compounds with each loss - there is no 'getting used to it'",
          "Anxiety in subsequent pregnancies is completely normal",
          "Seek a therapist who specializes in pregnancy loss or reproductive trauma",
          "Support organizations like RESOLVE and the Miscarriage Association offer community",
          "Partners grieve differently - communication is essential",
        ],
      },
      {
        title: "Trying again after loss",
        content:
          "When you're ready to try again - and only when you're ready - the good news is that the odds are often in your favor. Even among couples with unexplained recurrent loss (no cause found after full workup), the live birth rate in the next pregnancy is approximately 60-75%. Your doctor may recommend a 'tender loving care' approach for your next pregnancy: early and frequent monitoring, early ultrasounds for reassurance, easy access to your care team when anxiety spikes, and progesterone support. Some clinics offer specialized recurrent loss programs with dedicated nurses and more frequent check-ins. There is no single right timeline for trying again. Some couples want to try immediately; others need months to grieve and heal. Both choices are valid. The most important thing is that you feel supported and that any identified causes have been addressed.",
        bulletPoints: [
          "60-75% live birth rate even after unexplained recurrent loss",
          "Early monitoring and frequent ultrasounds provide reassurance",
          "There is no 'right' timeline - try again when you feel ready",
          "Ensure any identified causes have been treated before conceiving",
          "Ask your clinic about specialized recurrent loss support programs",
        ],
      },
    ],
    references: [
      {
        authors: "Practice Committee of ASRM",
        title:
          "Evaluation and treatment of recurrent pregnancy loss: a committee opinion. Fertility and Sterility (2012)",
      },
      {
        authors: "ESHRE Guideline Group on RPL",
        title:
          "ESHRE guideline: recurrent pregnancy loss. Human Reproduction Open (2018)",
      },
      {
        authors: "Rai & Regan",
        title: "Recurrent miscarriage. The Lancet (2006)",
      },
    ],
  },
} satisfies ContentBlock<"article">);

// ─── Article 3: Thyroid & Fertility ─────────────────────────────────

registerBlock({
  id: makeId("new-article-thyroid-fertility"),
  kind: "article",
  tags: ["thyroid", "her", "testing"] as ContentTag[],
  title: "Thyroid and fertility: the hidden connection",
  subtitle: "A small gland with a big impact on getting pregnant.",
  readTimeMinutes: 6,
  body: {
    quickSummary: [
      {
        title: "Thyroid affects every step.",
        description:
          "From ovulation to implantation to maintaining a pregnancy, thyroid hormones play a role at every stage.",
      },
      {
        title: "A simple blood test.",
        description:
          "Checking TSH and free T4 is quick, inexpensive, and can reveal issues that are highly treatable.",
      },
      {
        title: "Tighter targets for conception.",
        description:
          "The TSH range considered optimal for fertility (under 2.5 mIU/L) is narrower than the general population range.",
      },
    ],
    sections: [
      {
        title: "How thyroid affects fertility",
        content:
          "Your thyroid is a butterfly-shaped gland at the base of your neck, and it essentially sets the pace for your entire metabolism - including your reproductive system. Thyroid hormones (T3 and T4) influence the regularity of your menstrual cycle, the quality of ovulation, the receptivity of your uterine lining, and the ability to sustain an early pregnancy. When thyroid levels are off - either too high or too low - it can disrupt any of these processes. Thyroid disorders are surprisingly common in women of reproductive age, affecting about 2-4% of the population, and subclinical (mild) thyroid dysfunction is even more prevalent.",
        bulletPoints: [
          "Thyroid hormones regulate menstrual cycle regularity",
          "They influence ovulation quality and progesterone production",
          "Uterine lining receptivity depends partly on thyroid function",
          "About 2-4% of reproductive-age women have a thyroid disorder",
        ],
      },
      {
        title: "Hypothyroidism vs. hyperthyroidism",
        content:
          "Hypothyroidism (underactive thyroid) is far more common and more directly linked to fertility problems. It can cause irregular or absent periods, anovulation (failure to release an egg), luteal phase defects (where the second half of the cycle is too short for implantation), and elevated prolactin levels that further suppress ovulation. The most common cause is Hashimoto's thyroiditis, an autoimmune condition. Hyperthyroidism (overactive thyroid), usually from Graves' disease, is less common but can also cause menstrual irregularity, difficulty conceiving, and increased miscarriage risk. Both conditions can affect egg quality indirectly by disrupting the hormonal environment needed for healthy follicle development.",
        bulletPoints: [
          "Hypothyroidism: irregular cycles, anovulation, luteal phase defects",
          "Hashimoto's thyroiditis is the most common cause of hypothyroidism",
          "Hyperthyroidism: menstrual irregularity and increased miscarriage risk",
          "Graves' disease is the most common cause of hyperthyroidism",
          "Both conditions are treatable and manageable during conception",
        ],
      },
      {
        title: "TSH levels for conception",
        content:
          "Here's where it gets nuanced. The general population reference range for TSH is roughly 0.4-4.0 mIU/L, and many labs flag results as 'normal' anywhere in that range. But for women trying to conceive, most reproductive endocrinologists want TSH under 2.5 mIU/L - and ideally between 0.5 and 2.0 mIU/L. This tighter target is based on research showing that TSH levels above 2.5 are associated with longer time to conception, higher miscarriage rates, and potential impacts on fetal brain development in early pregnancy. If your TSH is 3.5 and your primary care doctor says it's 'normal,' it's worth asking your RE for their take - the fertility-specific target may be different.",
        bulletPoints: [
          "General 'normal' range: 0.4-4.0 mIU/L (too broad for fertility)",
          "Fertility-optimal TSH: under 2.5 mIU/L, ideally 0.5-2.0",
          "TSH above 2.5 is linked to longer time to conception",
          "Higher TSH is associated with increased miscarriage rates",
          "Always discuss your results with your RE, not just your PCP",
        ],
      },
      {
        title: "Testing and treatment",
        content:
          "Testing is straightforward: a blood draw measuring TSH and free T4 at minimum, plus thyroid peroxidase (TPO) antibodies if Hashimoto's is suspected. These can be done any day of your cycle. Treatment for hypothyroidism is levothyroxine (Synthroid, Levoxyl), a synthetic thyroid hormone taken daily on an empty stomach. Dose adjustments are made every 4-6 weeks based on repeat blood work until TSH reaches the target range. Most women feel the effects within a few weeks. For hyperthyroidism, treatment is more complex and may involve antithyroid medications (methimazole or PTU), with careful management during conception and pregnancy. The key point is that thyroid issues are among the most treatable causes of fertility problems - once identified, management is usually straightforward and highly effective.",
        bulletPoints: [
          "Basic panel: TSH, free T4, and TPO antibodies",
          "Levothyroxine is the standard treatment for hypothyroidism",
          "Dose adjustments every 4-6 weeks until TSH is optimized",
          "Testing can be done on any day of the menstrual cycle",
          "Treatment effects are usually felt within weeks",
        ],
      },
      {
        title: "Thyroid during pregnancy",
        content:
          "Getting your thyroid optimized before pregnancy is ideal, but monitoring doesn't stop once you conceive. Thyroid hormone demand increases by about 30-50% during pregnancy, which means many women who were stable on levothyroxine before conception will need a dose increase - often as early as 4-6 weeks of pregnancy. Your doctor should check TSH every 4 weeks during the first trimester and at least once per trimester after that. Uncontrolled hypothyroidism during pregnancy is associated with preeclampsia, placental abruption, preterm delivery, and impaired fetal neurological development. The good news is that with proper monitoring and dose adjustments, outcomes for women with treated thyroid disease are excellent - essentially the same as for women without thyroid issues.",
        bulletPoints: [
          "Thyroid hormone needs increase 30-50% during pregnancy",
          "Levothyroxine dose often needs increasing by 4-6 weeks of pregnancy",
          "TSH should be checked every 4 weeks in the first trimester",
          "Untreated hypothyroidism carries risks for both mother and baby",
          "With proper management, outcomes are equivalent to the general population",
        ],
      },
    ],
    references: [
      {
        authors: "Alexander et al.",
        title:
          "2017 Guidelines of the American Thyroid Association for the Diagnosis and Management of Thyroid Disease During Pregnancy and the Postpartum. Thyroid (2017)",
      },
      {
        authors: "Practice Committee of ASRM",
        title:
          "Subclinical hypothyroidism in the infertile female population: a guideline. Fertility and Sterility (2015)",
      },
      {
        authors: "Poppe et al.",
        title:
          "Thyroid disease and female reproduction. Clinical Endocrinology (2007)",
      },
    ],
  },
} satisfies ContentBlock<"article">);

// ─── Article 4: FET Guide ───────────────────────────────────────────

registerBlock({
  id: makeId("new-article-fet-guide"),
  kind: "article",
  tags: ["during-cycle", "post-cycle", "practical"] as ContentTag[],
  title: "Your FET guide: frozen embryo transfer",
  subtitle: "Everything you need to know about your transfer day.",
  readTimeMinutes: 7,
  body: {
    quickSummary: [
      {
        title: "Simpler than retrieval.",
        description:
          "A frozen embryo transfer is a short, painless procedure that doesn't require anesthesia or significant recovery time.",
      },
      {
        title: "Two cycle options.",
        description:
          "You'll either do a medicated cycle (hormones control timing) or a natural/modified natural cycle.",
      },
      {
        title: "Strong success rates.",
        description:
          "FET success rates are comparable to - and sometimes better than - fresh transfer rates at most clinics.",
      },
    ],
    sections: [
      {
        title: "What is a frozen embryo transfer?",
        content:
          "A frozen embryo transfer (FET) is the process of thawing a previously frozen embryo and placing it into the uterus. It's the final step in using embryos that were created and frozen during an IVF cycle. FET has become the preferred approach at many clinics because it separates the stimulation and retrieval phase (which is hard on your body) from the transfer phase, giving your uterus time to recover and your hormones time to normalize. The procedure itself is quick - about 10-15 minutes - and most patients describe it as similar to a Pap smear in terms of discomfort. No anesthesia is needed, and you can drive yourself home.",
        bulletPoints: [
          "Thawing and transferring a previously frozen embryo",
          "Separates retrieval stress from transfer for better conditions",
          "Quick procedure: about 10-15 minutes, no anesthesia needed",
          "Embryo thaw survival rates exceed 95% with modern vitrification",
        ],
      },
      {
        title: "Medicated vs. natural FET cycle",
        content:
          "There are two main approaches to preparing your uterine lining for transfer. In a medicated (or programmed) cycle, you take estrogen (pills, patches, or injections) starting early in your cycle to build the lining, then add progesterone (injections, suppositories, or both) at a specific time to prepare it for implantation. The transfer is scheduled based on the number of days of progesterone exposure. In a natural cycle FET, your body does the hormonal work on its own - your doctor monitors your natural ovulation with ultrasounds and blood work, then times the transfer to your natural progesterone rise. A modified natural cycle adds a trigger shot to control ovulation timing more precisely. Each approach has similar success rates, and your doctor will recommend one based on your cycle regularity, preference, and medical history.",
        bulletPoints: [
          "Medicated: estrogen builds the lining, progesterone primes it for implantation",
          "Natural: your body's own hormones prepare the lining, with monitoring",
          "Modified natural: natural cycle plus a trigger shot for precise timing",
          "Success rates are comparable across all three approaches",
          "Medicated cycles offer more scheduling flexibility",
        ],
      },
      {
        title: "The transfer procedure",
        content:
          "Transfer day itself is usually calm and straightforward. You'll arrive at the clinic with a comfortably full bladder (the fluid helps visualize the uterus on ultrasound). The embryologist will thaw your embryo that morning and confirm it survived the thaw - you'll often get to see a photo. Your doctor will use a thin, soft catheter threaded through the cervix to place the embryo in the uterus, guided by abdominal ultrasound. You might see the tiny white flash on the screen as the embryo is deposited. The whole thing takes about 10-15 minutes, and many patients say the full bladder was the most uncomfortable part. Afterward, you'll rest for a few minutes before heading home.",
        bulletPoints: [
          "Arrive with a comfortably full bladder for ultrasound guidance",
          "Embryo is thawed that morning - you'll likely see a photo",
          "Thin catheter is guided through the cervix under ultrasound",
          "Most patients describe minimal discomfort",
          "Brief rest period at the clinic, then home for the day",
        ],
      },
      {
        title: "The two-week wait",
        content:
          "After transfer, you enter the infamous two-week wait (TWW) - roughly 9-12 days until your beta hCG blood test. This is widely considered the hardest part of the entire IVF process. You'll continue progesterone support (and estrogen, if on a medicated cycle). Activity restrictions vary by clinic, but most modern evidence suggests normal activity is fine - no need for strict bed rest. Some patients experience light cramping or spotting, which can mean absolutely nothing or could be an implantation sign. The honest truth is that symptoms during the TWW are unreliable indicators either way. Try to stay busy, lean on your support system, and be gentle with yourself. It's okay to feel anxious.",
        bulletPoints: [
          "Beta hCG blood test typically 9-12 days after transfer",
          "Continue all prescribed medications through the wait",
          "Normal activity is generally fine - strict bed rest is not supported by evidence",
          "Symptoms during the TWW are unreliable indicators of outcome",
          "This is the hardest stretch emotionally - be kind to yourself",
        ],
      },
      {
        title: "Success rates",
        content:
          "FET success rates have improved dramatically with vitrification technology. For women under 35 transferring a single PGT-A-tested (chromosomally normal) embryo, implantation rates are typically 60-70%. Without PGT-A testing, rates are somewhat lower because some transferred embryos may be chromosomally abnormal. Age at the time of egg retrieval (not at the time of transfer) is the most important predictor. For women 35-37 at retrieval, rates are around 50-60% per transfer with a tested embryo. For ages 38-40, approximately 40-50%. These are per-transfer rates, so cumulative success across multiple transfers is higher. Your clinic can provide their specific FET success rates broken down by age group.",
        bulletPoints: [
          "Under 35 with PGT-A-tested embryo: 60-70% per transfer",
          "Ages 35-37 with tested embryo: 50-60% per transfer",
          "Ages 38-40 with tested embryo: 40-50% per transfer",
          "Age at retrieval matters more than age at transfer",
          "Cumulative rates across multiple transfers are significantly higher",
        ],
      },
      {
        title: "When FET is preferred over fresh transfer",
        content:
          "There are several situations where a freeze-all approach (freezing all embryos and doing FET later) is recommended over a fresh transfer. If you're at risk for ovarian hyperstimulation syndrome (OHSS), freezing all embryos allows your body to recover safely. If your progesterone was elevated on trigger day, your lining may be less receptive - FET avoids this issue. If you're doing PGT-A testing, embryos need to be frozen while awaiting results. Some research also suggests that the hormonal environment after stimulation may be suboptimal for implantation in general, giving FET a slight edge for many patients. The freeze-all strategy has become increasingly common, with many high-volume clinics now doing more FETs than fresh transfers.",
        bulletPoints: [
          "OHSS risk: freezing all embryos lets the body recover safely",
          "Elevated progesterone on trigger day: lining receptivity may be compromised",
          "PGT-A testing: embryos must be frozen while awaiting genetic results",
          "Post-stimulation hormonal environment may reduce fresh transfer success",
          "Many top clinics now perform more FETs than fresh transfers",
        ],
      },
    ],
    references: [
      {
        authors: "Roque et al.",
        title:
          "Fresh embryo transfer versus frozen embryo transfer in IVF/ICSI cycles: a systematic review and meta-analysis. Fertility and Sterility (2019)",
      },
      {
        authors: "Practice Committee of ASRM and SART",
        title:
          "Guidance on the limits to the number of embryos to transfer: a committee opinion. Fertility and Sterility (2021)",
      },
      {
        authors: "Wong et al.",
        title:
          "Frozen embryo transfers: reassessing the data. Fertility and Sterility (2014)",
      },
    ],
  },
} satisfies ContentBlock<"article">);

// ─── Article 5: Embryo Transfer Guide ───────────────────────────────

registerBlock({
  id: makeId("new-article-embryo-transfer-guide"),
  kind: "article",
  tags: ["ivf", "during-cycle", "post-cycle", "practical"] as ContentTag[],
  title: "Your embryo transfer guide",
  subtitle: "What to expect before, during, and after transfer.",
  readTimeMinutes: 6,
  body: {
    quickSummary: [
      {
        title: "The procedure is brief.",
        description:
          "Most embryo transfers take about 10–15 minutes and do not require anesthesia.",
      },
      {
        title: "Your clinic's instructions lead.",
        description:
          "Follow the clinic's guidance for bladder preparation, medications, and activity after transfer.",
      },
      {
        title: "The beta is the answer.",
        description:
          "The clinic beta hCG blood test is the reliable next milestone; symptoms cannot predict the result.",
      },
    ],
    sections: [
      {
        title: "Before transfer",
        content:
          "Your clinic will tell you how to prepare, including whether you should arrive with a comfortably full bladder and which medications to take. Bring the exact questions you want answered about progesterone, estrogen, activity, and the date of your beta blood test.",
        bulletPoints: [
          "Follow the clinic's medication instructions",
          "Confirm the arrival time and bladder instructions",
          "Ask when and where the beta hCG blood test will happen",
        ],
      },
      {
        title: "Transfer day",
        content:
          "A doctor guides a thin, flexible catheter through the cervix and places the embryo in the uterus, usually with ultrasound guidance. The procedure is generally quick. Mild pressure or cramping can happen, but your clinic should explain what to expect for your specific procedure.",
        bulletPoints: [
          "The transfer itself is usually about 10–15 minutes",
          "No anesthesia is typically needed",
          "Your clinic's instructions take priority over general guidance",
        ],
      },
      {
        title: "After transfer",
        content:
          "Continue prescribed medications exactly as directed until your clinic changes the plan. Light spotting, cramping, or no symptoms at all can happen during the wait and do not predict the outcome. Contact your clinic for urgent or unexpected symptoms according to its instructions.",
        bulletPoints: [
          "Continue progesterone and other prescribed support",
          "Keep the clinic beta date visible",
          "Trust the beta rather than symptom-spotting or early home tests",
        ],
      },
    ],
  },
} satisfies ContentBlock<"article">);

// ─── Article 6: IUI Guide ───────────────────────────────────────────

registerBlock({
  id: makeId("new-article-iui-guide"),
  kind: "article",
  tags: ["during-cycle", "clinic-visit", "practical"] as ContentTag[],
  title: "IUI: your first treatment option",
  subtitle: "A gentle first step when you need a little help.",
  readTimeMinutes: 6,
  body: {
    quickSummary: [
      {
        title: "Less invasive than IVF.",
        description:
          "IUI is a simpler, less expensive procedure that places washed sperm directly into the uterus around ovulation.",
      },
      {
        title: "Best for specific situations.",
        description:
          "IUI works best for unexplained infertility, mild male factor, or when using donor sperm.",
      },
      {
        title: "Know when to move on.",
        description:
          "Most doctors recommend 3-4 IUI cycles before considering IVF if IUI hasn't worked.",
      },
    ],
    sections: [
      {
        title: "What is IUI?",
        content:
          "Intrauterine insemination (IUI) is a fertility treatment that places concentrated, washed sperm directly into the uterus through a thin catheter, timed to coincide with ovulation. By bypassing the cervix and depositing sperm closer to the fallopian tubes, IUI gives sperm a head start. The 'washing' process is key - the lab separates the most motile, healthy sperm from the semen, removes seminal fluid and other debris, and concentrates them into a small volume. This means the best sperm are delivered right where they need to be. The procedure itself takes just a few minutes and feels similar to a Pap smear. Most women experience minimal cramping, if any.",
        bulletPoints: [
          "Washed, concentrated sperm placed directly into the uterus",
          "Timed to coincide with ovulation for maximum chance",
          "The washing process selects the healthiest, most motile sperm",
          "Quick procedure - just a few minutes, minimal discomfort",
          "Can be done with or without ovulation-stimulating medications",
        ],
      },
      {
        title: "Who is IUI for?",
        content:
          "IUI is often the first-line treatment for several situations. It's commonly recommended for couples with unexplained infertility (where all testing comes back normal but pregnancy isn't happening), mild male factor infertility (slightly low count or motility), cervical factor issues (where cervical mucus may be hostile to sperm), and for single women or same-sex couples using donor sperm. IUI can be done with your natural cycle (timed to your natural ovulation) or with ovulation-stimulating medications like letrozole (Femara) or clomiphene citrate (Clomid), which increase the number of eggs released and improve timing. Medicated IUI cycles have higher success rates than unmedicated ones.",
        bulletPoints: [
          "Unexplained infertility after basic testing is complete",
          "Mild male factor: slightly low count or motility",
          "Cervical factor: mucus issues that may block sperm",
          "Single women or same-sex couples using donor sperm",
          "Ovulation disorders when combined with ovulation-inducing medication",
        ],
      },
      {
        title: "The procedure step by step",
        content:
          "An IUI cycle follows a predictable pattern. If you're doing a medicated cycle, you'll start letrozole or clomid early in your cycle (typically days 3-7). Around day 10-12, you'll have a monitoring visit with ultrasound to check follicle development and blood work to check hormone levels. When a dominant follicle reaches about 18-20mm, you may be given a trigger shot (hCG) to induce ovulation, or your doctor may monitor for your natural LH surge. The IUI is scheduled about 24-36 hours after the trigger shot or LH surge. On the day of the procedure, your partner produces a semen sample (or a donor sample is thawed), the lab washes and prepares it (about 30-60 minutes), and then your doctor performs the insemination. You'll lie still for about 10 minutes afterward, then go about your day normally.",
        bulletPoints: [
          "Days 3-7: oral medication (letrozole or clomid) if doing a medicated cycle",
          "Day 10-12: monitoring ultrasound and blood work",
          "Trigger shot or natural LH surge detection",
          "IUI performed 24-36 hours after trigger or surge",
          "Sperm washing takes 30-60 minutes, insemination takes a few minutes",
        ],
      },
      {
        title: "Success rates and realistic expectations",
        content:
          "Setting realistic expectations about IUI is important. Per-cycle success rates for IUI are modest: about 10-15% per cycle for medicated IUI with partner sperm, and slightly higher (15-20%) with donor sperm (because donor sperm is pre-screened for quality). Unmedicated IUI has lower rates, around 5-10%. These numbers may seem low, but they're per cycle - cumulative success over 3-4 cycles is significantly better. For context, a healthy couple trying naturally has about a 15-20% chance per month, so medicated IUI approximates natural fertility rates. Age is the biggest factor: success rates are highest for women under 35 and decline meaningfully after 37-38. The total cost per IUI cycle ranges from $500-$4,000 depending on medications and monitoring, making it much more affordable than IVF.",
        bulletPoints: [
          "Medicated IUI with partner sperm: about 10-15% per cycle",
          "Medicated IUI with donor sperm: about 15-20% per cycle",
          "Unmedicated (natural cycle) IUI: about 5-10% per cycle",
          "Cumulative success over 3-4 cycles is considerably higher",
          "Cost per cycle: $500-$4,000, significantly less than IVF",
        ],
      },
      {
        title: "When to move to IVF",
        content:
          "Most reproductive endocrinologists recommend trying 3-4 IUI cycles before moving to IVF, though this varies based on your diagnosis, age, and circumstances. If you're over 38, your doctor may suggest fewer IUI attempts or going straight to IVF because of the time factor. IUI is also not appropriate for blocked fallopian tubes, severe male factor infertility, or advanced endometriosis - in these cases, IVF is the better starting point. The decision to move from IUI to IVF can feel like a big leap, but it's not a failure. IUI and IVF are different tools for different situations, and sometimes the gentler approach simply isn't enough. IVF success rates per cycle are substantially higher (40-60% for women under 35 with PGT-A-tested embryos), and for many patients, moving to IVF sooner rather than later is the more efficient path to their goal.",
        bulletPoints: [
          "Most doctors recommend 3-4 IUI cycles before considering IVF",
          "Over age 38: fewer IUI attempts may be recommended",
          "IUI is not appropriate for blocked tubes, severe male factor, or advanced endo",
          "Moving to IVF is not a failure - it's choosing the right tool for the situation",
          "IVF success rates are substantially higher per cycle than IUI",
        ],
      },
    ],
    references: [
      {
        authors: "Practice Committee of ASRM",
        title:
          "Role of tubal surgery in the era of assisted reproductive technology: a committee opinion. Fertility and Sterility (2021)",
      },
      {
        authors: "Cohlen et al.",
        title:
          "IUI: review and systematic assessment of the evidence that supports global recommendations. Human Reproduction Update (2018)",
      },
    ],
  },
} satisfies ContentBlock<"article">);

// ─── Article 7: Donor Eggs/Sperm ────────────────────────────────────

registerBlock({
  id: makeId("new-article-donor-guide"),
  kind: "article",
  tags: ["practical", "clinic-visit", "emotional"] as ContentTag[],
  title: "Using donor eggs or sperm",
  subtitle: "When building your family takes a different path.",
  readTimeMinutes: 8,
  body: {
    quickSummary: [
      {
        title: "A well-established path.",
        description:
          "Donor gametes have been used successfully for decades, with donor egg IVF achieving some of the highest success rates in reproductive medicine.",
      },
      {
        title: "Many ways to find a donor.",
        description:
          "You can use an anonymous donor through an agency or bank, a known donor, or a directed donor with legal protections.",
      },
      {
        title: "It's deeply personal.",
        description:
          "The emotional journey of using donor gametes is real and valid - support and community can make a meaningful difference.",
      },
    ],
    sections: [
      {
        title: "When donor gametes are recommended",
        content:
          "The decision to use donor eggs or donor sperm comes up in several scenarios. Donor eggs may be recommended for women with diminished ovarian reserve, premature ovarian insufficiency (POI), repeated IVF failures with their own eggs, carriers of serious genetic conditions, or women over 42-43 where the chance of success with their own eggs is very low. Donor sperm is used by single women, same-sex female couples, and couples facing severe male factor infertility (such as azoospermia where surgical sperm retrieval isn't successful) or heritable genetic conditions on the male side. For some people, the recommendation comes after a long journey of trying with their own gametes. For others - like single women or same-sex couples - using a donor is always part of the plan. Both paths are equally valid.",
        bulletPoints: [
          "Diminished ovarian reserve or premature ovarian insufficiency",
          "Repeated IVF failures or poor egg quality",
          "Severe male factor infertility unresponsive to treatment",
          "Carriers of serious heritable genetic conditions",
          "Single parents or same-sex couples building their families",
        ],
      },
      {
        title: "Choosing a donor: known vs. anonymous",
        content:
          "Donors can be known (a friend, family member, or someone you've met) or anonymous (selected through a donor bank or agency). With anonymous donors, you'll typically have access to detailed profiles including medical history, family health history, physical characteristics, education, personal essays, and sometimes childhood photos or audio interviews. Some banks offer 'open ID' or 'identity release' donors who agree to be contactable by donor-conceived offspring after age 18. Known donors can provide a sense of connection and allow a child to know their genetic origins from the start, but they require careful legal agreements and psychological counseling to navigate the complex relationship dynamics. There's no right or wrong choice - it depends on your values, circumstances, and what feels right for your future family.",
        bulletPoints: [
          "Anonymous donors: selected through banks with detailed profiles",
          "Open ID donors: agree to be contactable after offspring turns 18",
          "Known donors: a friend or family member, requires legal agreements",
          "Profiles typically include health history, photos, education, and personal essays",
          "Psychological counseling is recommended regardless of donor type",
        ],
      },
      {
        title: "The donor egg process",
        content:
          "Using donor eggs follows the IVF process, but the stimulation and retrieval happen to the donor rather than the intended parent. If using a fresh donor egg cycle, the donor's cycle is synchronized with the recipient's through medication so that the uterine lining is ready when the eggs are retrieved and fertilized. Alternatively, many people now use frozen donor eggs from an egg bank, which eliminates the need for synchronization and can be more affordable. The eggs are fertilized with sperm (partner or donor), cultured to the blastocyst stage, and transferred via FET. Success rates with donor eggs are among the highest in reproductive medicine - typically 50-65% per transfer for fresh donor egg cycles - because donors are young (usually 21-32) and thoroughly screened. Frozen donor egg cycles have slightly lower but still strong success rates.",
        bulletPoints: [
          "Fresh cycle: donor's retrieval is synchronized with your lining preparation",
          "Frozen eggs: purchased from an egg bank, no synchronization needed",
          "Eggs are fertilized, cultured, and transferred as in standard IVF",
          "Success rates: 50-65% per transfer with fresh donor eggs",
          "Donors are typically 21-32 years old and undergo rigorous screening",
        ],
      },
      {
        title: "The donor sperm process",
        content:
          "Using donor sperm is generally simpler and less expensive than donor eggs. Donor sperm is purchased from a licensed sperm bank (such as California Cryobank, Seattle Sperm Bank, or Fairfax Cryobank) and shipped to your clinic. Costs per vial typically range from $500-$1,200. Donor sperm can be used for IUI (the simplest approach), IVF, or even at-home insemination in some cases, though clinic-based procedures have higher success rates. FDA regulations require donor sperm to be quarantined for 6 months and retested before release. All reputable banks screen donors for infectious diseases, genetic conditions (increasingly through expanded carrier screening), medical history, and family health history. You'll want to purchase multiple vials if you're planning several attempts or want the option of using the same donor for future siblings.",
        bulletPoints: [
          "Purchased from licensed sperm banks and shipped to your clinic",
          "Cost per vial: typically $500-$1,200",
          "Can be used for IUI, IVF, or in some cases at-home insemination",
          "FDA requires 6-month quarantine and retesting before release",
          "Buy multiple vials if you want the same donor for siblings",
        ],
      },
      {
        title: "Legal considerations",
        content:
          "Legal protections are essential when using donor gametes, and the specifics vary significantly by state and country. In the US, anonymous donors from licensed banks have their parental rights terminated through the bank's consent process. For known donors, a formal legal agreement drafted by a reproductive attorney is critical - it should clearly establish that the donor has no parental rights or financial obligations, and that the intended parent(s) are the legal parents. Some states require a court order or adoption process even with a known donor agreement. If you're using a gestational carrier in addition to donor gametes, the legal landscape becomes even more complex. A consultation with a reproductive attorney in your state is not optional - it's a necessary step to protect everyone involved, including the future child.",
        bulletPoints: [
          "Anonymous bank donors: parental rights handled through the bank's process",
          "Known donors: require a formal legal agreement drafted by a reproductive attorney",
          "Laws vary significantly by state - legal advice specific to your jurisdiction is essential",
          "Establish legal parentage before or shortly after birth",
          "Legal clarity protects the donor, the parents, and the child",
        ],
      },
      {
        title: "The emotional journey and disclosure",
        content:
          "Using donor gametes often involves a grief process - grieving the genetic connection to your child that you expected to have. This is a real loss, and it deserves space and acknowledgment. Many people find that working with a therapist who specializes in third-party reproduction helps them process these feelings before, during, and after treatment. The question of disclosure - whether and when to tell your child about their donor origins - has shifted dramatically in recent years. The overwhelming consensus among psychologists, ethicists, and donor-conceived adults is that early, age-appropriate disclosure is best for the child's wellbeing and identity development. Children told early tend to integrate this information naturally, while late discovery can cause trust issues and identity confusion. Organizations like the Donor Sibling Registry can help connect donor-conceived families. Many parents find that once they hold their baby, the genetic connection matters far less than they feared - the bond you build through love, presence, and daily care is what makes a family.",
        bulletPoints: [
          "Grief over the expected genetic connection is normal and valid",
          "A therapist specializing in third-party reproduction can be invaluable",
          "Early, age-appropriate disclosure is recommended by experts",
          "Children told early integrate donor origins naturally into their identity",
          "The Donor Sibling Registry helps connect donor-conceived families",
        ],
      },
    ],
    references: [
      {
        authors: "Practice Committee of ASRM and SART",
        title:
          "Recommendations for gamete and embryo donation: a committee opinion. Fertility and Sterility (2021)",
      },
      {
        authors: "Readings et al.",
        title:
          "Children conceived through donor insemination: findings from a longitudinal study of family relationships. Human Reproduction (2011)",
      },
      {
        authors: "Ethics Committee of ASRM",
        title:
          "Informing offspring of their conception by gamete or embryo donation: an Ethics Committee opinion. Fertility and Sterility (2018)",
      },
    ],
  },
} satisfies ContentBlock<"article">);
